/**
 * Foundation · Copy — dialogo unico "Copia da…" (origine, ambito, anteprima, applica).
 * Generico: le origini e il calcolo del piano sono forniti dal modulo chiamante.
 */
import React, { useEffect, useState } from "react";
import { Copy } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { SuiteButton } from "@/components/suite";
import { CopySourcePicker } from "./CopySourcePicker";
import { CopyScopePicker } from "./CopyScopePicker";
import { CopyPlanSummary } from "./CopyPlanSummary";

export function CopyFromDialog({ open, onOpenChange, sources, dates, initialDates = [], options = [], buildPlan, onApply, formatDate, title = "Copia da…" }) {
  const [source, setSource] = useState({ id: sources[0]?.id });
  const [scope, setScope] = useState({ type: "days", dates: initialDates });
  const [overwrite, setOverwrite] = useState(false);
  const [opts, setOpts] = useState({});
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setScope(initialDates.length ? { type: "days", dates: initialDates } : { type: "month" });
    setPlan(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open || !source.id) return undefined;
    let alive = true;
    setError(null);
    Promise.resolve(buildPlan({ source, scope, overwrite, options: opts }))
      .then((p) => alive && setPlan(p))
      .catch((e) => alive && (setPlan(null), setError(e?.message || "Origine non disponibile")));
    return () => { alive = false; };
  }, [open, source, scope, overwrite, opts, buildPlan]);

  const apply = () => { if (plan?.changes.length) { onApply(plan); onOpenChange(false); } };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="copy-from-dialog" onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName !== "BUTTON") { e.preventDefault(); apply(); } }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Copy size={16} /> {title}</DialogTitle>
          <DialogDescription>Scegli da dove copiare e su quali giornate. Prima di applicare vedi esattamente cosa cambia.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 md:grid-cols-2">
          <CopySourcePicker sources={sources} value={source} onChange={setSource} />
          <div className="space-y-4">
            <CopyScopePicker dates={dates} value={scope} onChange={setScope} formatDate={formatDate} />
            <label className="flex items-center gap-2 text-xs text-suite-neutral-700">
              <Checkbox checked={overwrite} onCheckedChange={(v) => setOverwrite(!!v)} data-testid="copy-from-overwrite" /> Sovrascrivi le giornate già compilate
            </label>
            {options.map((o) => (
              <label key={o.id} className="flex items-center gap-2 text-xs text-suite-neutral-700">
                <Checkbox checked={!!opts[o.id]} onCheckedChange={(v) => setOpts((p) => ({ ...p, [o.id]: !!v }))} data-testid={`copy-from-option-${o.id}`} /> {o.label}
              </label>
            ))}
          </div>
        </div>
        <CopyPlanSummary plan={plan} error={error} formatDate={formatDate} />
        <div className="flex justify-end gap-2">
          <SuiteButton size="sm" variant="ghost" onClick={() => onOpenChange(false)} data-testid="copy-from-cancel">Annulla</SuiteButton>
          <SuiteButton size="sm" icon={Copy} onClick={apply} disabled={!plan?.changes.length} data-testid="copy-from-apply">
            Copia {plan?.changes.length || 0} giornate
          </SuiteButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CopyFromDialog;
